import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags/index.ts'
import * as nodes from '/vercel/path0/markdoc/nodes/index.ts'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer();

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: \"2020 annual report – third year in at Covalent\"\ndate: 2021-01-14T00:25:15-08:00\ncategories: [\"company\"]\nsubbrand_image: https://www.covalenthq.com/static/images/blog/202101/2020-annual-report.jpeg\ndescription: Read Covalent's 2020 annual report to learn about the company's growth and achievements, including 900% team expansion, 2300% increase in customers, new partnerships, and community building events. See what's in store for 2021 and join their community.\ntype: legacy\n---\n\nThree years ago, Covalent did not exist. Despite a challenging 2020 for everyone, Covalent and the entire crypto space has thrived. Covalent has scaled from a small project with two passionate founders to a project with global ambitions. Here's a letter I shared with our stakeholders highlighting the key moments that defined Covalent over the last twelve months.\n\n\n---\n\nHey guys,\n\nGanesh from Covalent here. 👋\n\nWe are well on our way into 2021, and the Covalent team wishes everyone a wonderful new year! Thank you all for your continued support.\n\n2020 was a big year for Covalent, and the most pivotal yet in our firm's history. We closed an oversubscribed private sale round, and have seen a massive increase in the number of customers using our blockchain data API. Our product was validated in the market, and our team expanded rapidly to take on the challenge of blockchain data inaccessibility.\n\nBelow are a few highlights from the past year.\n\n## Covalent's growth in 2020\n\n### 1. Team\n\n2020 was a year of hypergrowth for Covalent. We grew our headcount by 900%, from 2 co-founders to now a strong team of 18 including network architects, software engineers, product managers, marketers and more.\n\n### 2. Customers\n\nWe are happy to announce that this was the year we found product/market fit. We started the year with just 3 projects, and now have over 70 teams building atop our blockchain data API, representing a 2300% increase. About 500 developers on these teams use our API.\n\nOn top of all that, this was the year we became profitable, securing top enterprise clients such as Consensys Activate.\n\n### 3. Mature API stack\n\nWith the number of customers and their diverse use-cases, we've been able to expand the breadth and deepen the scope of our API. Covalent's unique data architecture enables new use-cases which we've been able to address and push the industy forward.\n\n\n### 4. Hackathons & Community Events\n\n\n![Image](https://www.covalenthq.com/static/images/blog/202010/covalent-dnd-launch.png)\n\n![Image](https://www.covalenthq.com/static/images/blog/202012/omw-launch-v2.png?v2)\n\n\nWe launched two successful crypto-data hackathons this year, Dungeons & Data and our One Million Wallets campaign. With thousands of participants, we were truly humbled and impressed to see the sheer creativity in the Covalent community.\n\n\n{% youtube videoId=\"zYIGrAh4Pgc\" /%}\n\n### 5. Covalent's Hidden Gems Podcast\n\nIn 2020, we lauched our podcast \"Hidden Gems\" - with a specific focus on the people, projects and data in the crypto industry. The metric we are most proud of is the number of people who've used these episodes to reference-check projects before taking on a new job.\n\n\n### 6. Blockchains Indexed\n\nCovalent believes in a multi-chain future. With this in mind, we were able to expand our blockchain data offerings. We offer the richest data for the Ethereum blockchain, and will be announcing 6 more blockchain partnerships.\n\n\n### 7. Community Growth\n\nThis year was the kickstart to us building a passionate community of blockchain data scientists within Covalent. Across our Telegram, Twitter and Discord channels, we've been able to grow from 50 to over 10,000 active members.\n\n\n\n\n### 8. Capturing Mindshare\n\nWe were busy capturing mindshare in the crypto community throughout all of 2020. A few high-profile events that we spoke at include EthDenver, EthSingapore, and Gitcoin KERNEL program.\n\nCheck out our EthDenver talk below:\n\n{% youtube videoId=\"LAt8ZbulY9c\" /%}\n\nWe were also able to contribute to exclusive publications such as the Defiant:\n\n\n![Image](https://www.covalenthq.com/static/images/blog/202101/thedefiant.png)\n\n\n\nCheck out the full blog post on the Defiant [here.](https://thedefiant.substack.com/p/how-climbing-mt-everest-drove-one-155)\n\n\n\n## Looking ahead\n\n2020 has been an amazing year for us. For 2021, we are looking to build on this solid foundation and continue our mission of bringing true transparency and visibility across all crypto-assets. We can't wait for you to see what we have planned.\n\nWe welcome you to join our mailing list or directly reach out to me.\n\nMuch love,\nGanesh Swami\nCo-Founder and CEO, Covalent\n\n\n\n";
const filepath = "/blog/2020-annual-report.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
